/**
 * RFC 7396 deep merge for dashboard specs.
 *
 * Semantics:
 * - `null` in patch → remove the key
 * - Array in patch → replace (not concat)
 * - Object in patch → recursive merge
 * - Primitive in patch → replace
 */
export function deepMergeSpec(
  base: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base }

  for (const key of Object.keys(patch)) {
    const patchValue = patch[key]

    if (patchValue === null) {
      // RFC 7396: null means delete
      delete result[key]
    } else if (Array.isArray(patchValue)) {
      // Arrays are replaced wholesale
      result[key] = patchValue
    } else if (isPlainObject(patchValue) && isPlainObject(result[key])) {
      // Recursive merge for nested objects
      result[key] = deepMergeSpec(
        result[key] as Record<string, unknown>,
        patchValue as Record<string, unknown>
      )
    } else {
      result[key] = patchValue
    }
  }

  return result
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

const DASHBOARD_COLLECTION_FIELDS = new Set(["bars", "lines", "areas", "columns"])

/**
 * Tolerant merge for AI-authored `yaml-edit` responses.
 *
 * The public edit contract is still RFC 7396. This recovery path handles two
 * recurring model mistakes without weakening ordinary merge semantics:
 * - `children: [~, ..., <partial>]` uses null as an "unchanged" placeholder.
 * - `bars: { color: ... }` supplies one partial object for every series.
 */
export function mergeDashboardYamlEdit(
  base: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base }

  for (const key of Object.keys(patch)) {
    const patchValue = patch[key]
    const baseValue = result[key]

    if (patchValue === null) {
      delete result[key]
      continue
    }

    if (Array.isArray(patchValue)) {
      const containsPartialChild =
        key === "children" && patchValue.some((item) => isPlainObject(item) && !("type" in item))
      if (
        Array.isArray(baseValue) &&
        (patchValue.some((item) => item === null) || containsPartialChild)
      ) {
        const merged = [...baseValue]
        patchValue.forEach((item, index) => {
          if (item === null) return
          if (isPlainObject(item) && isPlainObject(merged[index])) {
            merged[index] = mergeDashboardYamlEdit(merged[index] as Record<string, unknown>, item)
          } else {
            merged[index] = item
          }
        })
        result[key] = merged
      } else {
        result[key] = patchValue
      }
      continue
    }

    if (
      DASHBOARD_COLLECTION_FIELDS.has(key) &&
      isPlainObject(patchValue) &&
      Array.isArray(baseValue)
    ) {
      result[key] = baseValue.map((item) =>
        isPlainObject(item) ? mergeDashboardYamlEdit(item, patchValue) : item
      )
      continue
    }

    if (isPlainObject(patchValue) && isPlainObject(baseValue)) {
      result[key] = mergeDashboardYamlEdit(baseValue, patchValue)
    } else {
      result[key] = patchValue
    }
  }

  return result
}
